import { useQuery } from '@apollo/client';
import { useParams } from '@reach/router';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import VisibilitySensor from 'react-visibility-sensor';

import { useArticleEditor } from '@xing-com/crate-articles-article-editor';
import NotFound from '@xing-com/crate-articles-not-found';
import { useTracking } from '@xing-com/crate-communication-tracking';
import { useIsTopBarHidden } from '@xing-com/crate-hooks-use-is-top-bar-hidden';
import { TopBar } from '@xing-com/crate-layout-top-bar';
import { useFeature } from '@xing-com/hub';

import ArticleAuthorInfoBox from './components/article-author-info-box';
import ArticleAuthorProfile from './components/article-author-profile';
import ArticleCover from './components/article-cover';
import ArticleEditHeader from './components/article-edit-header';
import ArticleHelmet from './components/article-helmet';
import ArticleSocialInteractions from './components/article-social-interactions';
import ArticleViewerContent from './components/article-viewer-content';
import ErrorStatusBanner from './components/error-banner';
import LoadingState from './components/loading-state';
import {
  GetArticleByIdDocument,
  type GetArticleByIdQuery,
  type GetArticleByIdQueryVariables,
} from './graphql/get-article-query.gql-types';
import {
  validateShowPaywall,
  parseArticle,
  parseAuthor,
  unscrambleId,
} from './helpers';
import { useUserLoginState } from './hooks/user-login-state';
import { Styled } from './styles';
import { type ArticleViewerProps } from './types';

type PageViewTrackingType = {
  PropContextDimension1?: string;
  PropContextDimension2?: string;
  EventNewsArticleView?: number;
  PropNewsArticleId?: string;
  PropNewsProduct?: string;
  PropNewsPublisherId?: string;
  PropLoginState?: string;
};

type SocialScrollingTrackingType = {
  PropScrolling: string;
};

const ArticleViewer: React.FC<ArticleViewerProps> = ({
  article: articleProp,
}) => {
  const isPageLoaded = useRef(false);

  const userIsPremium = useFeature('isPremium');
  const userHasProJobsMembership = useFeature('hasProJobsMembership');

  const { isLoggedOut } = useUserLoginState();
  const { track: pageviewTracking } = useTracking<PageViewTrackingType>();
  const { track: socialScrollingTracking } =
    useTracking<SocialScrollingTrackingType>();

  const [wasSeen, setWasSeen] = useState(false);
  const [checkVisibilitySensor, setCheckVisibilitySensor] = useState(false);
  const intl = useIntl();
  const { id: articleSlug } = useParams();
  const isTopBarHidden = useIsTopBarHidden();

  const { article: contextArticle, updateArticle } = useArticleEditor();

  const [isFollowingAuthor, setIsFollowingAuthor] = useState<boolean>(false);

  const [showErrorBanner, setShowErrorBanner] = useState<boolean>(false);

  const skipArticleQuery = !articleSlug || !!articleProp;

  const { data, loading, error } = useQuery<
    GetArticleByIdQuery,
    GetArticleByIdQueryVariables
  >(GetArticleByIdDocument, {
    errorPolicy: 'all',
    nextFetchPolicy: 'cache-and-network',
    skip: skipArticleQuery,
    variables: {
      articleID: articleSlug,
    },
  });

  useEffect(() => {
    let timerId: NodeJS.Timeout;
    if (!loading) {
      timerId = setTimeout(() => {
        setCheckVisibilitySensor(true);
      }, 500);
    }

    return () => {
      clearTimeout(timerId);
    };
  }, [loading]);

  useEffect(() => {
    if (articleProp) {
      updateArticle(articleProp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleProp]);

  useEffect(() => {
    if (!loading && !isPageLoaded.current) {
      const articleQueryData = data?.articlesArticle;

      if (articleQueryData) {
        const articleData = parseArticle(data);
        updateArticle(articleData);
        setIsFollowingAuthor(
          data.articlesArticle?.context?.interactions?.isFollowed ?? false
        );

        const isInsiderPage =
          articleQueryData.context?.__typename === 'ContentInsiderPage';

        const pageType = isInsiderPage ? 'insider_pages' : 'publisher_pages';

        const channel = isInsiderPage
          ? 'wbm/News_Insider'
          : 'wbm/News_NewsPage';

        const author = parseAuthor(data);
        pageviewTracking({
          type: 'pageview',
          channel,
          page: `${channel}/article_detailpage`,
          PropContextDimension1: `news_${pageType}_FulltextArticleView`,
          PropContextDimension2: `news_${pageType}_article_editor_version3`,
          EventNewsArticleView: 1,
          PropNewsArticleId: unscrambleId(data.articlesArticle?.id ?? ''),
          PropNewsPublisherId: unscrambleId(author.id ?? ''),
          PropNewsProduct: pageType,
          PropLoginState: isLoggedOut ? 'logged_out' : undefined,
        });
      } else {
        pageviewTracking({
          type: 'pageview',
          channel: 'wbm/News_Insider',
          page: 'wbm/News_Insider/article_detailpage',
        });
      }

      isPageLoaded.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading]);

  if (loading && !error) {
    return <LoadingState />;
  }

  if (!loading && error) {
    return (
      <NotFound
        copy={intl.formatMessage({
          id: 'ARTICLES_ARTICLE_VIEWER_NOT_FOUND_MESSAGE',
        })}
      />
    );
  }

  if (!skipArticleQuery && !data?.articlesArticle) {
    return (
      <NotFound
        copy={intl.formatMessage({
          id: 'ARTICLES_ARTICLE_VIEWER_NOT_FOUND_MESSAGE',
        })}
      />
    );
  }

  const handleSocialVisible = (isVisible: boolean): void => {
    if (!isVisible || wasSeen) return;
    setWasSeen(true);
    socialScrollingTracking({
      type: 'interaction',
      event: 'PropScrolling',
      PropScrolling:
        'news_articledetailpage_bottom_end_of_article_fta_scroll_yes',
    });
  };

  const handleMutationError = (): void => {
    setShowErrorBanner(true);
  };

  const article = data ? parseArticle(data) : contextArticle;
  const author = data ? parseAuthor(data) : null;
  const canEdit = data?.articlesArticle?.context?.abilities.canEdit ?? false;

  const showPaywall =
    !canEdit &&
    validateShowPaywall(
      article.settings?.membership,
      userIsPremium ?? false,
      userHasProJobsMembership ?? false
    );

  return (
    <React.Fragment>
      <ArticleHelmet article={article} skip={skipArticleQuery} />

      {!isLoggedOut && !isTopBarHidden && (
        <TopBar
          title={intl.formatMessage({
            id: 'ARTICLES_ARTICLE_VIEWER_TOP_BAR_TITLE',
          })}
          backButtonUrlAriaLabelSection={intl.formatMessage({
            id: 'ARTICLES_ARTICLE_VIEWER_TOP_BAR_TITLE',
          })}
          type="subheader"
          data-qa="article-viewer-topbar"
          skipSearch
        />
      )}

      <Styled.PreviewContainer>
        <ArticleEditHeader
          show={canEdit}
          article={article}
          author={author}
          onError={handleMutationError}
        />

        <ArticleCover article={article} />

        <ArticleAuthorProfile
          author={author}
          publishedAt={article.publishedAt ?? undefined}
          isFollowingAuthor={isFollowingAuthor ?? false}
          onFollowingClick={setIsFollowingAuthor}
        />

        <ArticleViewerContent
          article={article}
          author={author}
          showPaywall={showPaywall}
        />

        {!showPaywall && (
          <VisibilitySensor
            active={checkVisibilitySensor && !wasSeen}
            onChange={handleSocialVisible}
          >
            <ArticleSocialInteractions
              data-qa="article-viewer-social-interactions"
              article={article}
            />
          </VisibilitySensor>
        )}

        <ArticleAuthorProfile
          author={author}
          isFollowingAuthor={isFollowingAuthor ?? false}
          onFollowingClick={setIsFollowingAuthor}
          position="footer"
        />

        <ArticleAuthorInfoBox articleId={article.id} author={author} />
      </Styled.PreviewContainer>

      <ErrorStatusBanner
        show={showErrorBanner}
        handleOnClose={() => setShowErrorBanner(false)}
      />
    </React.Fragment>
  );
};

export default ArticleViewer;
